
import { Card, CardContent } from '@/components/ui/card';
import { Star, MapPin, Utensils } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface Restaurant {
  id: string;
  name: string;
  description: string;
  image_url: string;
  rating: number;
  address: string;
  created_at: string;
  is_open: boolean;
  opening_hours: any;
  average_rating?: number;
}

interface MenuItem {
  id: string;
  name: string;
  description: string;
  price: number;
  restaurant_id: string;
  restaurant?: {
    name: string;
    address: string;
    is_open: boolean;
  };
}

interface SearchResultsProps {
  restaurants: Restaurant[];
  menuItems: MenuItem[];
  loading: boolean;
  onClose: () => void;
}

const SearchResults = ({ restaurants, menuItems, loading, onClose }: SearchResultsProps) => {
  const navigate = useNavigate();

  const handleRestaurantClick = (restaurantId: string) => {
    navigate(`/restaurant/${restaurantId}`);
    onClose();
  };

  const handleMenuItemClick = (restaurantId: string) => {
    navigate(`/restaurant/${restaurantId}`);
    onClose();
  };

  if (loading) {
    return (
      <div className="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-lg shadow-lg z-50 p-4">
        <div className="animate-pulse space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex gap-3">
              <div className="w-12 h-12 bg-gray-200 rounded"></div>
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                <div className="h-3 bg-gray-200 rounded w-1/2"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (restaurants.length === 0 && menuItems.length === 0) {
    return null;
  }

  return (
    <div className="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-96 overflow-y-auto">
      {restaurants.length > 0 && (
        <div className="p-3">
          <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1">
            <Utensils className="h-4 w-4" />
            Restaurants
          </h3>
          {restaurants.map((restaurant) => (
            <Card 
              key={restaurant.id} 
              className="mb-2 cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => handleRestaurantClick(restaurant.id)}
            >
              <CardContent className="p-3">
                <div className="flex gap-3">
                  {restaurant.image_url ? (
                    <img
                      src={restaurant.image_url}
                      alt={restaurant.name}
                      className="w-12 h-12 rounded object-cover"
                    />
                  ) : (
                    <div className="w-12 h-12 bg-gray-200 rounded flex items-center justify-center">
                      <Utensils className="h-6 w-6 text-gray-400" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium truncate">{restaurant.name}</h4>
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        restaurant.is_open ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {restaurant.is_open ? 'Open' : 'Closed'}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 truncate">{restaurant.description}</p>
                    <div className="flex items-center gap-3 mt-1">
                      <div className="flex items-center gap-1">
                        <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                        <span className="text-xs">{(restaurant.average_rating || restaurant.rating).toFixed(1)}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <MapPin className="h-3 w-3 text-gray-400" />
                        <span className="text-xs text-gray-500 truncate">{restaurant.address}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {menuItems.length > 0 && (
        <div className="p-3 border-t border-gray-100">
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Food Items</h3>
          {menuItems.map((item) => (
            <Card 
              key={item.id} 
              className="mb-2 cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => handleMenuItemClick(item.restaurant_id)}
            >
              <CardContent className="p-3">
                <div className="flex justify-between items-start">
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium truncate">{item.name}</h4>
                    <p className="text-sm text-gray-600 truncate">{item.description}</p>
                    <div className="flex items-center justify-between mt-1">
                      <span className="text-sm font-medium">D{item.price}</span>
                      {item.restaurant && (
                        <span className="text-xs text-gray-500 truncate">
                          at {item.restaurant.name}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default SearchResults;
